/**
 * Hero Banner Section Component
 * Handles play/pause functionality, video modals, and reduced motion support
 * Uses global VideoModal component for modal functionality
 */

class HeroBanner {
  constructor(section) {
    this.section = section;
    this.pauseButton = section.querySelector('[data-video-pause-button]');
    this.videoContainer = section.querySelector('[data-video-container]');
    this.pauseIcon = section.querySelector('[data-pause-icon]');
    this.playIcon = section.querySelector('[data-play-icon]');
    
    // Modal trigger elements
    this.modalTriggers = section.querySelectorAll('[data-video-modal-trigger]');
    
    // Initialize paused state from button data attribute (optional - modal can work without pause button)
    this.isPaused = this.pauseButton?.getAttribute('data-paused') === 'true';
    this.videoElement = null;
    this.videoMediaElement = null;
    
    // Bound method for modal close handler
    this._handleModalClose = this._handleModalClose.bind(this);

    this.init();
  }

  init() {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      this.pauseVideo();
      this.section.setAttribute('data-video-paused', 'true');
    } else {
      // Set initial section state (only if pause button exists)
      if (this.pauseButton) {
        this.section.setAttribute('data-video-paused', this.isPaused ? 'true' : 'false');
      }
    }

    // Wait for video element to be loaded (if video container exists)
    if (this.videoContainer) {
      this.waitForVideo();
    }

    // Set up pause button (if exists)
    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => {
        this.togglePlayPause();
      });
    }

    // Listen for reduced motion changes
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionQuery.addEventListener('change', (e) => {
      if (e.matches) {
        this.pauseVideo();
        this.section.setAttribute('data-video-paused', 'true');
      }
    });
    
    // Set up modal functionality
    this.setupModal();
  }

  waitForVideo() {
    // Check if video-media element is ready
    const checkInterval = setInterval(() => {
      this.videoMediaElement = this.videoContainer.querySelector('video-media');
      
      if (this.videoMediaElement) {
        clearInterval(checkInterval);
        
        // Wait for the actual video or iframe element
        const checkMediaInterval = setInterval(() => {
          this.videoElement = this.videoMediaElement.querySelector('video, iframe');
          
          if (this.videoElement) {
            clearInterval(checkMediaInterval);
            this.setupVideoListeners();
          }
        }, 100);
      }
    }, 100);

    // Clear interval after 5 seconds to prevent infinite loop
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 5000);
  }

  setupVideoListeners() {
    // For native video elements
    if (this.videoElement.tagName === 'VIDEO') {
      this.videoElement.addEventListener('play', () => {
        this.updateUIState(false);
      });

      this.videoElement.addEventListener('pause', () => {
        this.updateUIState(true);
      });
    }
  }

  togglePlayPause() {
    if (this.isPaused) {
      this.playVideo();
    } else {
      this.pauseVideo();
    }
  }

  playVideo() {
    if (!this.videoMediaElement) return;

    // Use the playerTarget from video-media if available
    if (this.videoMediaElement.player) {
      this.videoMediaElement.play();
    } else if (this.videoElement) {
      if (this.videoElement.tagName === 'VIDEO') {
        this.videoElement.play();
      } else if (this.videoElement.tagName === 'IFRAME') {
        // For YouTube/Vimeo iframes
        const src = this.videoElement.src;
        if (src.includes('youtube.com')) {
          this.videoElement.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else if (src.includes('vimeo.com')) {
          this.videoElement.contentWindow.postMessage('{"method":"play"}', '*');
        }
      }
    }

    this.updateUIState(false);
  }

  pauseVideo() {
    if (!this.videoMediaElement) return;

    // Use the playerTarget from video-media if available
    if (this.videoMediaElement.player) {
      this.videoMediaElement.pause();
    } else if (this.videoElement) {
      if (this.videoElement.tagName === 'VIDEO') {
        this.videoElement.pause();
      } else if (this.videoElement.tagName === 'IFRAME') {
        // For YouTube/Vimeo iframes
        const src = this.videoElement.src;
        if (src.includes('youtube.com')) {
          this.videoElement.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (src.includes('vimeo.com')) {
          this.videoElement.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }
    }

    this.updateUIState(true);
  }

  updateUIState(paused) {
    this.isPaused = paused;
    
    if (paused) {
      this.section.setAttribute('data-video-paused', 'true');
      if (this.pauseButton) {
        this.pauseButton.setAttribute('data-paused', 'true');
        this.pauseButton.setAttribute('aria-label', 'Play video');
      }
      if (this.pauseIcon) this.pauseIcon.style.display = 'none';
      if (this.playIcon) this.playIcon.style.display = 'flex';
    } else {
      this.section.setAttribute('data-video-paused', 'false');
      if (this.pauseButton) {
        this.pauseButton.setAttribute('data-paused', 'false');
        this.pauseButton.setAttribute('aria-label', 'Pause video');
      }
      if (this.pauseIcon) this.pauseIcon.style.display = 'flex';
      if (this.playIcon) this.playIcon.style.display = 'none';
    }
  }

  /**
   * Set up modal functionality using global VideoModal component
   */
  setupModal() {
    if (this.modalTriggers.length === 0) return;

    // Listen for modal close to optionally resume background video
    document.addEventListener('videoModalClosed', this._handleModalClose);

    // Open modal on any trigger click
    this.modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.openModal(trigger);
      });
    });
  }

  /**
   * Handle modal close event
   */
  _handleModalClose(event) {
    // Check if the trigger was from this section
    const triggerElement = event.detail?.triggerElement;
    if (triggerElement && this.section.contains(triggerElement)) {
      // Optionally resume background video when modal closes
      // Uncomment if desired:
      // this.playVideo();
    }
  }

  /**
   * Open the video modal using global VideoModal
   */
  openModal(trigger) {
    // Pause background video when opening modal
    this.pauseVideo();

    // Get video configuration from trigger data attributes
    const modalVideoType = trigger.dataset.modalVideoType || 'vimeo';
    const vimeoId = trigger.dataset.modalVimeoId;
    const shopifyVideoSrc = trigger.dataset.modalShopifyVideoSrc;
    const modalSize = trigger.dataset.modalSize || 'fullscreen';

    // If no dedicated modal video, try to get from background video
    let videoType = modalVideoType;
    let videoId = vimeoId;
    let videoSrc = shopifyVideoSrc;

    if (!vimeoId && !shopifyVideoSrc) {
      // Try to extract from background video
      const videoMedia = this.section.querySelector('video-media');
      if (videoMedia) {
        const host = videoMedia.getAttribute('host');
        if (host === 'vimeo') {
          videoType = 'vimeo';
          const iframe = this.section.querySelector('iframe');
          if (iframe) {
            const originalSrc = iframe.getAttribute('src') || '';
            const match = originalSrc.match(/player\.vimeo\.com\/video\/([^?]+)/);
            if (match && match[1]) {
              videoId = match[1];
            }
          }
        }
      }
    }

    // Open global video modal
    if (window.VideoModal && (videoId || videoSrc)) {
      window.VideoModal.open({
        type: videoType,
        vimeoId: videoId,
        shopifyVideoSrc: videoSrc,
        size: modalSize,
        triggerElement: trigger
      });
    }
  }
}

// Initialize all hero banner sections
document.addEventListener('DOMContentLoaded', () => {
  const heroBannerSections = document.querySelectorAll('.hero-banner:not([data-hero-banner-initialized])');
  heroBannerSections.forEach(section => {
    section.setAttribute('data-hero-banner-initialized', 'true');
    new HeroBanner(section);
  });
});

// Handle Shopify theme editor section load/unload
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.hero-banner:not([data-hero-banner-initialized])');
    if (section) {
      section.setAttribute('data-hero-banner-initialized', 'true');
      new HeroBanner(section);
    }
  });
}
